declare function gtag(...args: any[]): void;
// path: frontend/src/pages/Home.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useQuizGenerator } from "../hooks/useQuizGenerator";
import { VOICE_OPTIONS } from "../constants/voices";
import { auth } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";
import { useCredits } from "../context/CreditProvider";
import { getSelectedBg, listenSelectedBg } from "../utils/bgSelection";
import { API_BASE_URL } from "@/config";
import { VoiceSelector } from "@/components/VoiceSelector";  // ✅ ADD THIS LINE

export type OutputMode = "tiktok" | "full";
export type ExplanationMode = "answer_only" | "full";
export type RenderMode = "mcq" | "qa_only";

const PRESETS: Record<
  OutputMode,
  { fmt: "16:9" | "9:16" | "1:1"; dur: number; explanation: ExplanationMode }
> = {
  tiktok: { fmt: "9:16", dur: 6, explanation: "answer_only" },
  full: { fmt: "16:9", dur: 15, explanation: "full" },
};

function inferLanguageCode(voice?: string): string | undefined {
  if (!voice) return;
  const m = voice.match(/^([a-z]{2,4}-[A-Z]{2})-/);
  return m ? m[1] : undefined;
}

function extractVideoUrl(body: any): string | undefined {
  if (!body) return undefined;

  if (typeof body === "string") return body;

  const flat =
    body.url ??
    body.video_url ??
    body.videoUrl ??
    body.path ??
    body.last_render_url ??
    body.lastRenderUrl;
  if (typeof flat === "string") return flat;

  const nests = [
    body.data?.url,
    body.data?.video_url,
    body.data?.videoUrl,
    body.result?.url,
    body.result?.video_url,
    body.result?.videoUrl,
    body.session?.last_render_url,
    body.session?.url,
  ].filter(Boolean);
  if (nests.length && typeof nests[0] === "string") return nests[0] as string;

  return undefined;
}

/**
 * Format background value for backend validation
 * Backend expects: http://, https://, or /static/
 */
function formatBackgroundForBackend(background: string): string {
  if (!background) return "";

  if (background.startsWith("http://") || background.startsWith("https://")) {
    return background;
  }

  if (background.startsWith("/static/")) {
    return background;
  }

  if (background.endsWith(".mp4") && !background.includes("/")) {
    return `/static/${background}`;
  }

  return background;
}

function formatBackgroundLabel(background: string): string {
  if (!background || background === "None") {
    return "None selected";
  }

  let filename = background;

  try {
    if (
      background.startsWith("http://") ||
      background.startsWith("https://")
    ) {
      filename = new URL(background).pathname.split("/").pop() || background;
    } else {
      filename = background.split("/").pop() || background;
    }

    filename = decodeURIComponent(filename);
  } catch {
    filename = background.split("/").pop() || background;
  }

  return filename
    .replace(/\.[^.]+$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}


const VIDEO_STEPS = [
  { icon: "🧠", label: "Generating question..." },
  { icon: "🎙️", label: "Synthesizing voice..." },
  { icon: "🎬", label: "Rendering video..." },
  { icon: "☁️", label: "Uploading to cloud..." },
  { icon: "✅", label: "Finalizing video..." },
];

function VideoGeneratingWidget() {
  const [step, setStep] = useState(0);
  const [dots, setDots] = useState(0);
  useEffect(() => {
    const stepTimer = setInterval(() => setStep(s => (s + 1) % VIDEO_STEPS.length), 4000);
    const dotTimer = setInterval(() => setDots(d => (d + 1) % 4), 500);
    return () => { clearInterval(stepTimer); clearInterval(dotTimer); };
  }, []);
  return (
    <div className="rounded-2xl border border-blue-200 dark:border-blue-800 bg-gradient-to-b from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 p-6 flex flex-col items-center gap-5 shadow-lg">
      <div className="relative flex items-center justify-center w-24 h-24">
        <div className="absolute w-24 h-24 rounded-full bg-blue-400/20 animate-ping" style={{ animationDuration: "1.5s" }} />
        <div className="absolute w-16 h-16 rounded-full bg-blue-400/30 animate-ping" style={{ animationDuration: "1.5s", animationDelay: "0.3s" }} />
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xl text-2xl">
          {VIDEO_STEPS[step].icon}
        </div>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
          {VIDEO_STEPS[step].label}{".".repeat(dots)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your video is being crafted</p>
      </div>
      <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-full h-1.5 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-[4000ms] ease-in-out"
          style={{ width: `${((step + 1) / VIDEO_STEPS.length) * 100}%` }}
        />
      </div>
      <div className="flex gap-2">
        {VIDEO_STEPS.map((s, i) => (
          <div key={i} className={`w-2 h-2 rounded-full transition-all duration-500 ${i === step ? "bg-blue-500 scale-125" : i < step ? "bg-blue-300" : "bg-gray-200 dark:bg-gray-700"}`} />
        ))}
      </div>
      <p className="text-xs text-center text-gray-400 dark:text-gray-500 px-2">
        You can leave this page and check My Videos when your video is ready.
      </p>
    </div>
  );
}

export default function Home() {
  const [topic, setTopic] = useState("cell biology");
  const [voice, setVoice] = useState(VOICE_OPTIONS[0]?.id ?? "");
  const [duration, setDuration] = useState(8);
  const [numQuestions, setNumQuestions] = useState(5);
  const [videoFormat, setVideoFormat] = useState<"16:9" | "9:16" | "1:1">(
    "16:9"
  );
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);

  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [customWatermarkImage, setCustomWatermarkImage] = useState("");
  const [watermarkText, setWatermarkText] = useState("AutoQuiz AI");
  const [watermarkWidth, setWatermarkWidth] = useState(200);
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.9);

  const [outputMode, setOutputMode] = useState<OutputMode>(
    () => (localStorage.getItem("output_mode") as OutputMode) || "tiktok"
  );
  const [explanation, setExplanation] = useState<ExplanationMode>(
    () =>
      (localStorage.getItem("explanation_mode") as ExplanationMode) ||
      PRESETS.tiktok.explanation
  );

  // ✅ Home page always generates MCQ-style videos
  // (AnimatedQuiz page can switch between mcq / qa_only)
  const renderMode: RenderMode = "mcq";

  const [generating, setGenerating] = useState(false);
  const [, setUserPlan] = useState<"free" | "starter" | "pro">("free");

  const { user } = useAuth();
  const navigate = useNavigate();

  const { loading, fetchQuiz } = useQuizGenerator();
  const { setCredits, refreshCredits } = useCredits();

  // Authenticated quota check – backend enforces the real limits
  const checkQuota = useMemo(
    () => async (): Promise<{ remaining: number; limit: number }> => {
      const currentUser = auth?.currentUser;
      if (!currentUser) {
        throw new Error("You must be signed in to check quota.");
      }

      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/payment/checkQuota`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Authentication required. Please sign in again.");
        }
        const text = await res.text();
        throw new Error(
          `Quota check failed (${res.status}): ${text || res.statusText}`
        );
      }

      const json = await res.json();

      // Extract plan info
      if (json?.plan) {
        setUserPlan(json.plan);
      }

      const remaining = json?.videos?.remaining ?? json?.remaining;
      const limit = json?.videos?.limit ?? json?.limit;

      if (!Number.isFinite(remaining) || !Number.isFinite(limit)) {
        throw new Error("Quota response is invalid.");
      }

      return { remaining, limit };
    },
    [setCredits]
  );

  // Background selection (shared across pages)
  const [templateBg, setTemplateBg] = useState(getSelectedBg());
  useEffect(() => {
    const stop = listenSelectedBg(setTemplateBg);
    return stop;
  }, []);

  // Load user plan on page load
  useEffect(() => {
    if (!user) {
      setUserPlan("free");
      return;
    }

    checkQuota()
      .then(({ remaining, limit }) => {
        setCredits(remaining, limit);
      })
      .catch((err) => {
        console.error("Failed to load user plan:", err);
        setUserPlan("free");
      });
  }, [user, checkQuota, setCredits]);

  // Restore last topic
  useEffect(() => {
    const saved = localStorage.getItem("last_quiz");
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.topic === "Generate a high-yield basic principle of pathology quiz for USMLE Step 1 prep. Make it accurate and error-free.") {
        setTopic("cell biology");
      } else if (parsed?.topic) {
        setTopic(parsed.topic);
      }
    } catch {
      // ignore corrupted localStorage
    }
  }, []);

  // Apply presets
  useEffect(() => {
    const p = PRESETS[outputMode];
    setVideoFormat(p.fmt);
    setDuration(p.dur);
    setExplanation(p.explanation);
    localStorage.setItem("output_mode", outputMode);
  }, [outputMode]);

  useEffect(() => {
    localStorage.setItem("explanation_mode", explanation);
  }, [explanation]);

  const ensureQuestions = async () => {
    // Home page should always fetch MCQ questions (options + answer)
    // If your hook supports an options arg for mode, pass it here.
    const qs = await fetchQuiz(topic, numQuestions, { language_code: inferLanguageCode(voice) || 'en-US' });
    if (!qs.length) {
      throw new Error("No valid questions returned.");
    }
    return qs;
  };

  const handleWatermarkUpload = async (file: File) => {
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      toast.error("Please sign in to upload a custom watermark.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${API_BASE_URL}/api/video/upload-background`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Watermark upload failed.");
      }

      const result = await response.json();
      const uploadedUrl = result.url || result.background_url;

      if (!uploadedUrl) {
        throw new Error("No URL returned from watermark upload.");
      }

      const staticPath = uploadedUrl.includes("/static/")
        ? uploadedUrl.substring(uploadedUrl.indexOf("/static/"))
        : uploadedUrl;

      setCustomWatermarkImage(staticPath);
      toast.success("Watermark logo uploaded.");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Watermark upload failed."
      );
    }
  };

  const maybeUploadBackground = async (): Promise<string> => {
    // Priority order: uploaded file > animated bg in localStorage > templateBg
    const savedAnimatedBg = localStorage.getItem("selected_bg");
    let bgName = savedAnimatedBg || templateBg || "";

    console.log("🎨 Background resolution:");
    console.log("  - Animated BG (localStorage):", savedAnimatedBg);
    console.log("  - Template BG (state):", templateBg);
    console.log("  - Uploaded file:", backgroundFile?.name || "None");

    if (backgroundFile) {
      const MAX_SIZE_MB = 200;
      if (backgroundFile.size > MAX_SIZE_MB * 1024 * 1024) {
        throw new Error(`Background file is too large (>${MAX_SIZE_MB} MB).`);
      }

      const currentUser = auth?.currentUser;
      if (!currentUser) {
        throw new Error("You must be signed in to upload backgrounds.");
      }

      const token = await currentUser.getIdToken();
      const fd = new FormData();
      fd.append("file", backgroundFile);

      const up = await fetch(`${API_BASE_URL}/api/video/upload-background`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      if (!up.ok) {
        const text = await up.text();
        throw new Error(
          `Upload failed (${up.status}): ${text || up.statusText}`
        );
      }

      const uj = await up.json();
      bgName = uj.filename || uj.url || uj.path || backgroundFile.name;
      console.log("  ✅ Using uploaded file:", bgName);
    } else if (savedAnimatedBg) {
      console.log("  ✅ Using saved animated background:", bgName);
    } else if (templateBg) {
      console.log("  ✅ Using template background:", bgName);
    } else {
      console.log("  ℹ️ No background specified, using default.");
    }

    return bgName;
  };

  const postRender = async (_mode: OutputMode, payload: any): Promise<string> => {
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      throw new Error("You must be signed in to generate videos.");
    }

    const token = await currentUser.getIdToken();
    const renderRequestId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const resp = await fetch(`${API_BASE_URL}/api/video/generate-video`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "Idempotency-Key": `video:${currentUser.uid}:${renderRequestId}`,
      },
      body: JSON.stringify(payload),
    });

    const raw = await resp.text();
    let body: any = null;
    try {
      body = raw ? JSON.parse(raw) : null;
    } catch {
      body = raw;
    }

    console.log("🔎 generate-video response body:", body);

    if (!resp.ok) {
      if (resp.status === 401) {
        throw new Error("Authentication required. Please sign in and try again.");
      }

      if (resp.status === 403) {
        if (body && typeof body === "object") {
          if (
            body.detail &&
            typeof body.detail === "object" &&
            body.detail.message
          ) {
            throw new Error(body.detail.message);
          }
          if (body.message) throw new Error(body.message);
          if (body.error) throw new Error(body.error);
          if (typeof body.detail === "string") throw new Error(body.detail);
        }
        throw new Error(
          "You do not have enough credits to generate this video. Add credits and try again."
        );
      }

      const msg =
        (body &&
          typeof body === "object" &&
          (body.detail?.message || body.detail || body.message || body.error)) ||
        raw ||
        `${resp.status} ${resp.statusText}`;
      throw new Error(msg);
    }

    const rawUrl = extractVideoUrl(body);
    if (!rawUrl) {
      throw new Error("Backend did not return a video URL.");
    }
    return String(rawUrl);
  };

  const buildBasicSocialMetadata = (
    topicValue: string,
    questionsValue: any[]
  ) => {
    const cleanTopic = topicValue.trim();
    const firstQuestion = questionsValue
      .map((question) => String(question?.question || "").trim())
      .find(Boolean);

    const title = cleanTopic
      ? `${cleanTopic} Quiz Challenge`
      : "Test Your Knowledge";

    const description = cleanTopic
      ? `Test your knowledge of ${cleanTopic} with this educational quiz from AutoQuiz AI.`
      : "Test your knowledge with this educational quiz from AutoQuiz AI.";

    const hook = firstQuestion
      ? `Can you answer this before the reveal? ${firstQuestion}`
      : "Can you get the answer before the reveal?";

    const topicTag = cleanTopic
      .replace(/[^A-Za-z0-9]/g, "")
      .trim();

    const hashtags = [
      topicTag ? `#${topicTag}` : "",
      "#Quiz",
      "#Study",
      "#Education",
      "#AutoQuizAI",
    ].filter(Boolean);

    return {
      title,
      description,
      short_caption: [
        hook,
        "",
        hashtags.join(" "),
      ].join("\n"),
      long_caption: [
        hook,
        "",
        description,
        "",
        "Comment your score and follow for more quizzes.",
        "",
        hashtags.join(" "),
      ].join("\n"),
      hashtags,
      og_title: title,
      og_description: description,
    };
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a quiz topic.");
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    if (generating) return;

    setGenerating(true);
    try {
      const { remaining, limit } = await checkQuota();
      setCredits(remaining, limit);

      if (remaining <= 0) {
        toast.error(
          "You do not have enough credits to generate this video. Add credits and try again."
        );
        return;
      }

      const questions = await ensureQuestions();
      const rawBackground = await maybeUploadBackground();

      const formattedBackground = formatBackgroundForBackend(rawBackground);

      console.log("🎬 Background before formatting:", rawBackground);
      console.log("🎬 Background after formatting:", formattedBackground);

      const payload = {
        // ✅ Make render mode explicit for the backend router + services
        render_mode: renderMode,

        questions,
        voice,

        background: formattedBackground,
        background_url: formattedBackground,

        num_questions: questions.length,
        video_format: videoFormat,

        output_mode: outputMode,
        explanation_mode: explanation,

        tiktok_friendly: outputMode === "tiktok",
        language_code: inferLanguageCode(voice),

        // ✅ Keep overrides, but ensure we don’t accidentally drift into qa_only
        // ✅ Also pass countdown_seconds so your "Duration per question" actually affects rendering
        overrides: {
          watermark_on: watermarkEnabled,
          watermark_text: watermarkEnabled ? watermarkText.trim() : "",
          ...(watermarkEnabled && customWatermarkImage.trim()
            ? { watermark_image: customWatermarkImage.trim() }
            : {}),
          watermark_w: watermarkWidth,
          watermark_opacity: watermarkOpacity,
          render_mode: renderMode,
          countdown_seconds: duration,
        },
      };

      console.log("📦 Full payload being sent:", JSON.stringify(payload, null, 2));

      const url = await postRender(outputMode, payload);

      localStorage.setItem(
        "last_quiz",
        JSON.stringify({ topic, questions, video_url: url, ts: Date.now() })
      );
      refreshCredits().catch(() => { });
      // GA4 conversion event
      if (typeof gtag !== "undefined") gtag("event", "video_generated", { topic, voice, format: videoFormat });
      const socialMetadata = buildBasicSocialMetadata(
        topic,
        questions
      );

      navigate("/video", {
        state: {
          url,
          social_metadata: socialMetadata,
        },
      });
    } catch (e: any) {
      console.error("❌ Video generation error:", e);

      const message = String(e?.message || "");
      const connectionInterrupted =
        e instanceof TypeError ||
        /load failed|failed to fetch|network.*(failed|lost)|connection.*lost/i.test(
          message
        );

      if (connectionInterrupted) {
        toast.error(
          "The connection was interrupted, but your video may still be rendering. Check My Videos."
        );
        refreshCredits().catch(() => {});
        navigate("/dashboard");
        return;
      }

      toast.error(message || "Video generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!topic.trim()) return;
    try {
      await fetchQuiz(topic, numQuestions, { language_code: inferLanguageCode(voice) || 'en-US' });
      toast.success("Regenerated quiz questions.");
    } catch {
      toast.error("Quiz regeneration failed.");
    }
  };

  const totalSeconds = useMemo(
    () => duration * Math.max(1, numQuestions),
    [duration, numQuestions]
  );

  const currentBg = localStorage.getItem("selected_bg") || templateBg || "None";

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">AutoQuiz AI</h1>
        <p className="text-gray-600 dark:text-gray-300">
          Generate faceless quiz videos in one click.
        </p>
      </div>

      {/* Two-column layout: Form + Sidebar */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content - Left (2/3 width on desktop) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Presets */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Preset:
            </span>
            {(["tiktok", "full"] as OutputMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setOutputMode(m)}
                className={[
                  "px-3 py-1.5 rounded-full border",
                  outputMode === m
                    ? "bg-black text-white dark:bg-white dark:text-black"
                    : "hover:bg-gray-50 dark:hover:bg-gray-800",
                ].join(" ")}
                title={
                  m === "tiktok"
                    ? "Short, answer-only, 9:16"
                    : "Full explanation, 16:9"
                }
                aria-pressed={outputMode === m}
              >
                {m === "tiktok" ? "TikTok / Reels" : "Full (Blog/YouTube)"}
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-500">
              Est. length: ~{Math.round(totalSeconds)}s
            </span>
          </div>

          <Link to="/backgrounds" className="text-blue-600 hover:underline">
            Choose Built-in Background
          </Link>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md space-y-6">
            {/* Topic */}
            <div>
              <label className="block mb-2 text-sm font-medium">Quiz Topic</label>
              <textarea
                rows={3}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-3 rounded-md shadow-sm resize-none"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., cell biology, anatomy, physiology, pathology, pharmacology…"
              />
            </div>

            {/* Voice */}
            <div>
              <label className="block mb-2 text-sm font-medium">Voice</label>
              <VoiceSelector
                value={voice}
                onChange={setVoice}
              />
            </div>
            {/* Watermark */}
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-700 dark:bg-purple-900/20">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={watermarkEnabled}
                  onChange={(e) => setWatermarkEnabled(e.target.checked)}
                  className="h-4 w-4"
                />
                <div>
                  <div className="font-medium">Enable watermark</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Add your own text or logo, or leave this off for no watermark.
                  </div>
                </div>
              </label>

              {watermarkEnabled && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium">
                      Watermark text
                    </label>
                    <input
                      type="text"
                      value={watermarkText}
                      onChange={(e) => setWatermarkText(e.target.value)}
                      placeholder="AutoQuiz AI"
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">
                      Custom logo
                    </label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      <input
                        type="text"
                        value={customWatermarkImage}
                        onChange={(e) => setCustomWatermarkImage(e.target.value)}
                        placeholder="/static/assets/your-logo.png"
                        className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <label className="cursor-pointer rounded-md border border-purple-500 px-4 py-2 text-sm font-medium">
                        Upload
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handleWatermarkUpload(file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <label>
                      <span className="block text-sm font-medium">
                        Logo width
                      </span>
                      <input
                        type="number"
                        min={80}
                        max={640}
                        value={watermarkWidth}
                        onChange={(e) =>
                          setWatermarkWidth(
                            Math.max(80, Math.min(640, Number(e.target.value)))
                          )
                        }
                        className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </label>

                    <label>
                      <span className="block text-sm font-medium">
                        Opacity
                      </span>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.1}
                        value={watermarkOpacity}
                        onChange={(e) =>
                          setWatermarkOpacity(
                            Math.max(0, Math.min(1, Number(e.target.value)))
                          )
                        }
                        className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </label>
                  </div>

                  <div className="rounded-md bg-white p-3 text-sm dark:bg-gray-800">
                    <span className="font-medium">Preview: </span>
                    {customWatermarkImage
                      ? "Custom logo uploaded"
                      : watermarkText || "AutoQuiz AI"}
                  </div>

                  {customWatermarkImage && (
                    <button
                      type="button"
                      onClick={() => setCustomWatermarkImage("")}
                      className="text-sm text-red-600 underline dark:text-red-400"
                    >
                      Clear custom logo
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Explanation */}
            <div>
              <label className="block mb-2 text-sm font-medium">Explanation</label>
              <select
                value={explanation}
                onChange={(e) =>
                  setExplanation(e.target.value as ExplanationMode)
                }
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-3 rounded-md shadow-sm"
              >
                <option value="answer_only">Answer only (short)</option>
                <option value="full">Full explanation</option>
              </select>
            </div>

            {/* Duration */}
            <div>
              <label className="block mb-2 text-sm font-medium">
                Duration per question (sec)
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-3 rounded-md shadow-sm"
              >
                {(outputMode === "tiktok"
                  ? [4, 5, 6, 8, 10]
                  : [8, 10, 12, 15, 20, 30, 45, 60, 90]
                ).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* Num Questions */}
            <div>
              <label className="block mb-2 text-sm font-medium">
                Number of Questions
              </label>
              <input
                type="number"
                min={1}
                max={10}
                value={numQuestions}
                onChange={(e) => setNumQuestions(Number(e.target.value))}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-3 rounded-md shadow-sm"
              />
            </div>

            {/* Format */}
            <div>
              <label className="block mb-2 text-sm font-medium">Video Format</label>
              <select
                value={videoFormat}
                onChange={(e) =>
                  setVideoFormat(e.target.value as "16:9" | "9:16" | "1:1")
                }
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-3 rounded-md shadow-sm"
              >
                <option value="16:9">YouTube (16:9)</option>
                <option value="9:16">TikTok/Reels (9:16)</option>
                <option value="1:1">Instagram Feed (1:1)</option>
              </select>
            </div>

            {/* Background Upload */}
            <div>
              <label className="block mb-2 text-sm font-medium">
                Custom Background (MP4)
              </label>
              <input
                type="file"
                accept="video/mp4"
                onChange={(e) => setBackgroundFile(e.target.files?.[0] ?? null)}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-3 rounded-md shadow-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Current background:{" "}
                <span className="font-medium">
                  {backgroundFile?.name || formatBackgroundLabel(currentBg)}
                </span>
              </p>
            </div>

            {/* Generate Button */}
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={handleGenerate}
                disabled={generating || loading || !topic.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md shadow w-full disabled:opacity-50"
              >
                {generating ? "Generating…" : "Generate Video — 5 credits"}
              </button>

              <button
                onClick={handleRegenerate}
                disabled={loading || !topic.trim()}
                className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-md shadow w-full disabled:opacity-50"
              >
                {loading ? "Regenerating…" : "Regenerate Questions — Free"}
              </button>
            </div>

            {generating && (
              <div className="mt-3 p-4 rounded-xl bg-blue-600 text-white text-sm flex items-start gap-3 shadow-lg">
                <span className="text-xl">🎬</span>
                <div>
                  <p className="font-semibold">Video is being generated!</p>
                  <p className="text-blue-100 text-xs mt-0.5">You can leave this page and check My Videos when it’s ready. Estimated time: ~1-2 min.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">

          {generating && <VideoGeneratingWidget />}
        </div>
      </div>
    </div>
  );
}


